import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Select from 'components/Steps/List';
import Bubbles from 'components/Steps/Bubbles';
import BubbleChart from 'components/Steps/BubbleChart';
import Tagcloud from 'components/Steps/Tagcloud';
import BarChart from 'components/Steps/BarChart';
import PieChart from 'components/Steps/PieChart';
import CustomCard from 'components/Steps/CustomCard';
import Faces from 'components/Steps/Faces';
import Deliverables from 'components/Steps/Deliverables';
import FormElement from 'components/Steps/FormElement';
import FreeHtml from 'components/Steps/FreeHtml';
import Inputs from 'components/Steps/Inputs';
import SwipeableCards from 'components/Steps/SwipeableCards';
import CreationThinkSeed from 'components/CreationThinkSeed';
import { NarrativeStepStyled, Title, StepWidth } from './styles';

function NarrativeStep(props) {
  const {
    data, actualContext, onFinish, onChangeAnswers, app,
  } = props;

  let color = app.color1;

  if (actualContext) {
    color = typeof actualContext.color === 'string' ? actualContext.color : actualContext.color.color;
  }

  const finishStep = () => {
    if (onFinish) {
      onFinish();
    }
  };

  const getSelectStep = () => {
    return (
      <Select
        key='step'
        type={data.element_type}
        narrativeType={data.narrative_type}
        values={data.answers}
        options={data.elements}
        onChangeAnswers={onChangeAnswers}
        maxAnswers={typeof data.more_info.max_answers !== 'undefined' ? parseInt(data.more_info.max_answers, 10) : null}
        maxOptions={typeof data.more_info.max_options !== 'undefined' ? parseInt(data.more_info.max_options, 10) : null}
        canCreate={typeof data.more_info.can_create !== 'undefined' ? !!data.more_info.can_create : false}
        showSearcher={typeof data.more_info.show_searcher !== 'undefined' ? !!data.more_info.show_searcher : false}
        stepId={data.id}
      />
    );
  };

  const getFacesStep = () => {
    return (
      <Faces
        backgroundColorText={color}
        values={data.answers || []}
        color={color}
        options={data.users}
        select={data.more_info.select}
        sort={data.more_info.sort}
        actionSelect={data.more_info.action}
        sortField={data.more_info.action}
        onChangeAnswers={onChangeAnswers}
      />
    );
  };

  const getTopElementsStep = () => {
    const options = _.map(data.elements, ({
      name, amount, image, description, users_likes: userLikes, users,
    }) => ({
      id: name,
      label: name,
      value: amount,
      color,
      image,
      description,
      users,
      users_likes: userLikes,
    }));


    switch (data.visualization) {
      case 'bubbles':
        return (<Bubbles elements={data.elements} />);
      case 'pie_chart':
        return (<PieChart backgroundColor={color} color={color.color || '#fff'} options={options} />);
      case 'bar_chart':
        return (<BarChart options={options} color={color} backgroundColor={color} />);
      case 'bubble_chart':
        return (<BubbleChart options={options} color={color} />);
      case 'tagcloud':
        return (<Tagcloud options={options} color={color} />);
      case 'faces':
        return (<Faces options={options} color={color} />);
      default:
        return (<Tagcloud options={options} color={color} />);
    }
  };

  const getCustomStep = () => {
    return (<CustomCard data={data} color={color} type={data.element_type} />);
  };

  const getDeliverablesStep = () => {
    return (<Deliverables elements={data.elements} />);
  };

  const getCreationFormStep = () => {
    return (
      <FormElement
        color={color}
        narrativeType={data.narrative_type}
        elementType={data.element_type}
        onFinish={finishStep}
      />
    );
  };

  const getInputsStep = () => {
    return (
      <Inputs
        narrativeType={data.narrative_type}
        elementType={data.element_type}
        onChangeAnswers={onChangeAnswers}
        values={data.answers || []}
        stepId={data.id}
      />
    );
  };

  const getFeedbackStep = () => {
    const usersIds = data.users ? _.map(data.users, user => user.id) : [];
    const thinkSeedsIds = data.think_seeds ? _.map(data.think_seeds, thinkSeed => thinkSeed.id) : [];
    return (<SwipeableCards users={usersIds} thinkSeeds={thinkSeedsIds} onEnd={finishStep} />);
  };

  const getThinkSeedCreationStep = () => {
    return (<CreationThinkSeed color={color} data={data} />);
  };

  const getFreeStep = () => {
    return (<FreeHtml html={data.content} />);
  };

  const printStep = () => {
    let title = null;
    if (data.title) {
      title = (<Title key='title' color={color} dangerouslySetInnerHTML={{ __html: data.title }} />);
    }
    let stepContent = null;
    switch (data.type) {
      case 'select':
        stepContent = getSelectStep();
        break;
      case 'top_elements':
        stepContent = getTopElementsStep();
        break;
      case 'custom':
        stepContent = getCustomStep();
        break;
      case 'faces':
        stepContent = getFacesStep();
        break;
      case 'deliverables':
        stepContent = getDeliverablesStep();
        break;
      case 'creation_form':
        stepContent = getCreationFormStep();
        break;
      case 'input':
        stepContent = getInputsStep();
        break;
      case 'feedback_users':
        stepContent = getFeedbackStep();
        break;
      case 'creation_think_seed':
        stepContent = getThinkSeedCreationStep();
        break;
      case 'free':
        stepContent = getFreeStep();
        break;
      default:
        stepContent = (<div>Not controlled</div>);
    }

    return (
      <React.Fragment>
        <StepWidth type={data.type}>
          {title}
          {stepContent}
        </StepWidth>
      </React.Fragment>
    );
  };

  return (
    <NarrativeStepStyled>
      {printStep()}
    </NarrativeStepStyled>
  );
}

NarrativeStep.propTypes = {
  data: PropTypes.object.isRequired,
  actualContext: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  onFinish: PropTypes.func,
  onChangeAnswers: PropTypes.func,
};

NarrativeStep.defaultProps = {
  onFinish: null,
  onChangeAnswers: null,
};

export default NarrativeStep;



// WEBPACK FOOTER //
// ./src/components/NarrativeStep/components/index.jsx